/**
 * Full HD is the reference composition.
 *
 * Above full HD the server serves every stylesheet's `px` and `rem` as that
 * length times the screen's scale over full HD (game/utils/uiScaleCss.js), so
 * a QHD or 4K screen shows the full-HD layout bigger instead of a thumbnail of
 * it; at full HD and below the stylesheets are exactly as authored. Script
 * that writes a pixel length, or reasons with one next to measured geometry,
 * uses the same scale so its numbers stay in step with the styles:
 *
 * - `uiCssPx(59)` for a length written into a style — CSS keeps it in step
 *   through window resizes with no listener;
 * - `uiPx(16)` for arithmetic against measured boxes, read at the moment of use.
 *
 * The scale is 1 whenever the viewport is at most 1920 wide or 900 tall, so
 * phones, tablets and laptops are untouched. It depends on the viewport's
 * CSS size only, never on the device pixel ratio.
 */

export const UI_REFERENCE_WIDTH = 1920;
/* The page height of a full-HD browser window, not the screen's 1080: see
   game/utils/uiScaleCss.js for why width decides and height only caps. */
export const UI_REFERENCE_HEIGHT = 900;

/* The divisors as served — a millionth over, so float rounding in an engine
   can never lift the viewport term past the authored length at exactly full
   HD (game/utils/uiScaleCss.js). */
const WIDTH_DIVISOR = '1920.002';
const HEIGHT_DIVISOR = '900.001';

/** The scale over full HD of a viewport of this CSS size: 1 at full HD and below. */
export function uiScaleOf(width: number, height: number): number {
  if (!(width > 0) || !(height > 0)) return 1;
  return Math.max(1, Math.min(width / UI_REFERENCE_WIDTH, height / UI_REFERENCE_HEIGHT));
}

/** The screen's scale over full HD: 1 at full HD and below. */
export function uiScale(): number {
  return uiScaleOf(Number(window.innerWidth), Number(window.innerHeight));
}

/** A full-HD pixel length in this screen's CSS pixels, now. */
export function uiPx(px: number): number {
  return px * uiScale();
}

/**
 * A full-HD pixel length as a CSS value that follows the viewport — character
 * for character what game/utils/uiScaleCss.js serves for an authored `<px>px`,
 * so a stylesheet pass leaves it alone. The amount sits inside min()/max()
 * because Firefox truncates those to 1/60px (see that module).
 */
export function uiCssPx(px: number): string {
  if (px === 0) return '0px';
  const [outer, inner] = px < 0 ? ['min', 'max'] : ['max', 'min'];
  return `${outer}(${px}px, ${inner}(${px} * 100vw / ${WIDTH_DIVISOR}, ${px} * 100vh / ${HEIGHT_DIVISOR}))`;
}

/**
 * The pixel unit the served stylesheets multiply a transform's lengths by above
 * full HD (game/utils/uiScaleCss.js, SCALE_UNIT). Under CSS `zoom` WebKit leaves
 * a viewport-relative length in a transform unzoomed, and every hover
 * enlargement zooms its card, so those lengths are plain pixels times this:
 * `1px` at full HD and below, where the copies never apply, `2px` at 4K.
 * Setting it switches off the viewport spelling they fall back on without it.
 */
export const UI_SCALE_UNIT = '--mf-ui-px';
export const UI_SCALE_UNIT_MISSING = '--mf-ui-px-missing';

function syncScaleUnit(): void {
  const root = document.documentElement;
  if (!root) return;
  const value = `${uiScale()}px`;
  if (root.style.getPropertyValue(UI_SCALE_UNIT) !== value) root.style.setProperty(UI_SCALE_UNIT, value);
  if (root.style.getPropertyValue(UI_SCALE_UNIT_MISSING) !== '0') root.style.setProperty(UI_SCALE_UNIT_MISSING, '0');
}

/* Once per window, from the first module that loads this one. A resize event
   is handled before that frame's style, so the unit never trails the viewport;
   until it is set the stylesheets fall back to the viewport unit itself. */
if (typeof window !== 'undefined' && typeof document !== 'undefined' && !window.__moonfallScaleUnitWired) {
  window.__moonfallScaleUnitWired = true;
  syncScaleUnit();
  window.addEventListener('resize', syncScaleUnit, { passive: true });
}

const NUMBER = '([+-]?(?:\\d+\\.?\\d*|\\.\\d+)(?:e[+-]?\\d+)?)';
const SCALED_PX_TEXT = new RegExp(
  `^(?:max|min)\\(\\s*${NUMBER}px\\s*,\\s*(?:min|max)\\(\\s*${NUMBER}\\s*\\*\\s*100vw\\s*/\\s*${WIDTH_DIVISOR.replace('.', '\\.')}`
  + `\\s*,\\s*${NUMBER}\\s*\\*\\s*100vh\\s*/\\s*${HEIGHT_DIVISOR.replace('.', '\\.')}\\s*\\)\\s*\\)$`,
  'i',
);

/* The spelling of the above-full-HD copies: `min(60*5vw/96,60vh/9)`. */
const OVERRIDE_PX_TEXT = new RegExp(
  `^(?:max|min)\\(\\s*${NUMBER}\\s*\\*\\s*5vw\\s*/\\s*96\\s*,\\s*${NUMBER}vh\\s*/\\s*9\\s*\\)$`,
  'i',
);

/**
 * A length read back from CSS text, in this screen's CSS pixels.
 *
 * A custom property computes to its TEXT, and above full HD the server writes
 * an authored `60px` as `min(60*5vw/96,60vh/9)` (and uiCssPx as
 * `max(60px, min(60 * 100vw / 1920, …))`), which `parseFloat` reads as NaN —
 * a reader would silently drop to its fallback. This reads every spelling;
 * anything else parses exactly as `parseFloat` always did.
 */
export function cssLengthPx(value: unknown): number {
  const text = String(value ?? '').trim();
  const scaled = SCALED_PX_TEXT.exec(text) || OVERRIDE_PX_TEXT.exec(text);
  return scaled ? Number(scaled[1]) * uiScale() : Number.parseFloat(text);
}
